import { useEffect, useMemo, useState } from 'react';

import { PushPin } from '@mui/icons-material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Button, Grid, IconButton, Typography } from '@mui/material';
import { GridRowModel, GridRowOrderChangeParams } from '@mui/x-data-grid-pro';
import { capitalizeFirstLetters } from '@npi/buddy-utils';
import { ConfigurationStatus } from '@prisma/client';

import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';

import { Flex } from 'src/components/Flex';
import Loader from 'src/components/Loader/Loader';
import { ModalBox } from 'src/components/ModalContent/Modal';
import { NPIDataGrid, NPIDataGridColDef } from 'src/components/NPIDataGrid/NPIDataGrid';
import { createColumnDef } from 'src/components/NPIDataGrid/NPIDataGrid.utils';
import ResourceSectionForm, {
    ResourceSectionType,
} from 'src/components/ResourceSectionForm/ResourceSectionForm';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import StatusChip, { STATUS_CHIP_TYPES } from 'src/components/UserStatusChip/UserStatusChip';
import { ADD_PARTNER_RESOURCES_SECTION } from 'src/graphql/mutations/addPartnerResources';
import { DELETE_RESOURCE_SECTION } from 'src/graphql/mutations/deleteResourceSection.mutation';
import { EDIT_PARTNER_RESOURCE_SECTION_MUTATION } from 'src/graphql/mutations/editPartnerResources.mutation';
import { GET_PARTNER_RESOURCES_QUERY } from 'src/graphql/queries/partnerResources.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { LogError } from 'src/lib';
import { formatDate } from 'src/utils/utils';
import { partnerResourceSectionValidationSchema } from 'src/utils/validations';
import { ResourceSection } from 'types/graphql';

const ConfigurePartnerResourcesPage = () => {
    const [showDeleteResourceModal, setShowDeleteResourceModal] = useState({
        open: false,
        id: null,
        sectionName: '',
    });
    const [showAddEditResourceModal, setShowAddEditResourceModal] = useState(false);
    const [initialValues, setInitialValues] = useState<ResourceSectionType>({
        sectionName: '',
        status: ConfigurationStatus.ACTIVE,
        pinned: false,
    });
    const [hasPinned, setHasPinned] = useState(false);
    const [pinnedSectionName, setPinnedSectionName] = useState('');
    const [isViewResourceSectionModal, setIsViewResourceSectionModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('Add New Section');
    const [isEdit, setIsEdit] = useState(false);

    const {
        data,
        loading,
        refetch: fetchLatestSections,
    } = useQuery(GET_PARTNER_RESOURCES_QUERY, {
        variables: { configuring: true },
        fetchPolicy: 'cache-and-network',
        onError: (err) => {
            showErrorToast(err.message);
            LogError(err);
        },
    });

    const getSectionData = (id: number) => {
        if (!id) return [];

        return data?.getPartnerResources?.filter((section: { id: number }) => {
            return section.id === id;
        });
    };

    const [deleteResourceSectionMutation] = useMutation(DELETE_RESOURCE_SECTION, {
        onCompleted: () => {
            fetchLatestSections();
            showSuccessToast(`${showDeleteResourceModal.sectionName} section has been deleted`);
            setShowDeleteResourceModal({ open: false, id: null, sectionName: '' });
        },
        onError: () => {
            showErrorToast('Error Deleting Partner Section');
        },
    });

    const partnerResourceSections = useMemo(() => {
        return data?.getPartnerResources || [];
    }, [data]);

    useEffect(() => {
        const pinnedSection = partnerResourceSections.find(({ pinnedAt }) => {
            return Boolean(pinnedAt);
        });

        if (pinnedSection) {
            setHasPinned(true);
            setPinnedSectionName(capitalizeFirstLetters(pinnedSection.name));
        } else {
            setHasPinned(false);
        }
    }, [partnerResourceSections]);

    const tableData = useMemo(() => {
        const sections = partnerResourceSections;

        const result =
            [...sections]?.map((section) => {
                return {
                    id: section?.id,
                    name: section?.name,
                    status: section?.status,
                    addedBy: section?.createdByName,
                    createdAt: section?.createdAt,
                    pinnedAt: section?.pinnedAt,
                    order: section?.order,
                };
            }) ?? [];

        const rowsData: GridRowModel[] = [];
        const pinnedRowsData: { top: GridRowModel[] } = {
            top: [],
        };

        result.forEach((row) => {
            if (row.pinnedAt) {
                pinnedRowsData.top.push(row);
            } else {
                rowsData.push(row);
            }
        });

        return {
            rows: result,
            pinnedRows: pinnedRowsData,
        };
    }, [partnerResourceSections]);

    const [addPartnerResourceSection] = useMutation(ADD_PARTNER_RESOURCES_SECTION, {
        onCompleted: () => {
            fetchLatestSections();
            showSuccessToast('Resource Section Added!');
            setShowAddEditResourceModal(false);
        },
        onError: () => {
            showErrorToast('Error Creating Partner Resource Section');
        },
    });

    const [editPartnerResourceSection] = useMutation(EDIT_PARTNER_RESOURCE_SECTION_MUTATION, {
        onCompleted: () => {
            fetchLatestSections();
            showSuccessToast('Section has been updated.');
            setShowAddEditResourceModal(false);
        },
        onError: () => {
            showErrorToast('Error Updating Partner Resource Section');
        },
    });

    const [updatePartnerResourceSectionOrder] = useMutation(
        EDIT_PARTNER_RESOURCE_SECTION_MUTATION,
        {
            onError: () => {
                showErrorToast('Error Updating Partner Resource Section Order');
            },
        }
    );

    const manageSection = (formik: ResourceSectionType) => {
        const { id, sectionName, status, pinned } = formik;
        const pinnedAt = pinned ? new Date() : null;

        if (isEdit) {
            editPartnerResourceSection({
                variables: {
                    input: {
                        id,
                        name: sectionName,
                        status,
                        pinnedAt,
                    },
                },
            });
        } else {
            addPartnerResourceSection({
                variables: {
                    input: {
                        name: sectionName,
                        status,
                        pinnedAt,
                    },
                },
            });
        }
    };

    const handleSubmit = (data: ResourceSectionType) => {
        const { status, pinned, id } = data;

        if (status === 'INACTIVE' && pinned) {
            showErrorToast('A pinned section cannot be inactive.');
            return;
        }

        if (status === 'INACTIVE' && !isDeletable(id).isDeleteable) {
            showErrorToast('Unable to make section inactive while it contains active resources.');
            return;
        }

        if (hasPinned && pinned) {
            showErrorToast(
                `Only one section can be pinned at a time, remove the pin from '${pinnedSectionName}' and try again.`
            );
            return;
        }

        manageSection(data);
    };

    const handleAddSection = () => {
        setInitialValues({ sectionName: '', status: ConfigurationStatus.ACTIVE, pinned: false });
        setModalTitle('Add New Section');
        setIsViewResourceSectionModal(false);
        setShowAddEditResourceModal(true);
        setIsEdit(false);
    };

    const handleRecord = (selectedSection: ResourceSection, isEdit = false) => {
        const [selectedSectionDetails] = getSectionData(selectedSection?.id);
        const { name, status, pinnedAt, id } = selectedSectionDetails;

        let pinned = true;
        if (pinnedAt === null) {
            pinned = false;
        }

        setInitialValues({ sectionName: name, status, pinned, id });
        setModalTitle(`${isEdit ? 'Edit' : 'View'} Section`);
        setIsViewResourceSectionModal(!isEdit);
        setShowAddEditResourceModal(true);
        setIsEdit(isEdit);
    };

    /**
     * @param id
     * @returns true if a section has no active resources linked to it
     */
    const isDeletable = (id: number) => {
        const section = partnerResourceSections.find((section) => {
            return section.id === id;
        });

        if (section) {
            const hasActiveResource = section.resources.some((resource) => {
                return resource.status === ConfigurationStatus.ACTIVE;
            });
            return { isDeleteable: !hasActiveResource, sectionName: section.name };
        }

        return { isDeleteable: true, sectionName: '' };
    };

    const deleteRecord = (id: number) => {
        const { isDeleteable, sectionName } = isDeletable(Number(id));
        if (isDeleteable) {
            setShowDeleteResourceModal({ open: true, id, sectionName });
        } else {
            showErrorToast('Unable to delete section while it contains active resources');
        }
    };

    const handleRowOrderChange = async (params: GridRowOrderChangeParams) => {
        changeRowOrder(params, tableData);

        const { rows } = tableData;
        await Promise.all(
            rows.map((row) => {
                return updatePartnerResourceSectionOrder({
                    variables: {
                        input: {
                            id: row.id,
                            order: row.order,
                        },
                    },
                });
            })
        );

        showSuccessToast('Section order has been updated.');
    };

    const PARTNER_RESOURCES_TABLE_HEADERS: NPIDataGridColDef[] = [
        createColumnDef({
            field: 'name',
            headerName: 'Section Name',
            renderCell: (params) => {
                const row = params?.row;
                return (
                    <Flex alignItems="center" gap={1}>
                        {row?.pinnedAt && (
                            <PushPin sx={{ position: 'absolute', left: '14px', bottom: '20px' }} />
                        )}
                        {row?.name}
                    </Flex>
                );
            },
        }),
        createColumnDef({
            field: 'status',
            headerName: 'Status',
            renderCell: (params) => {
                const status = params?.row?.status;
                return <StatusChip status={status} type={STATUS_CHIP_TYPES.PARTNER_RESOURCE} />;
            },
        }),
        createColumnDef({ field: 'addedBy', headerName: 'Added By' }),
        createColumnDef({
            field: 'createdAt',
            headerName: 'Date Added',
            renderCell: (params) => {
                const date = new Date(params?.row?.createdAt);
                return formatDate({ date });
            },
        }),
        // TODO: (KM) Make this generic to the NPIDataGrid
        createColumnDef({
            field: 'actions',
            flex: 0,
            minWidth: 160,
            renderCell: (params) => {
                return (
                    <Flex gap="8px">
                        <IconButton aria-label="edit">
                            <EditOutlinedIcon
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRecord(params.row, true);
                                }}
                            />
                        </IconButton>
                        <IconButton aria-label="delete">
                            <DeleteOutlineOutlinedIcon
                                onClick={(e) => {
                                    e.stopPropagation();
                                    return deleteRecord(params.row.id);
                                }}
                            />
                        </IconButton>
                    </Flex>
                );
            },
        }),
    ];

    return (
        <PageContent
            heading="Configure Partner Resources"
            subHeading="Add, View, Remove and Update resource sections"
        >
            <MetaTags
                title="Configure Partner Resources"
                description="Configure Partner Resources page"
            />

            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    {loading || !data ? (
                        <Loader />
                    ) : (
                        <NPIDataGrid
                            dataTestId="partner-resources-table"
                            rows={tableData.rows}
                            columns={PARTNER_RESOURCES_TABLE_HEADERS}
                            handleView={(row) => {
                                handleRecord(row);
                            }}
                            getRowId={(row) => {
                                return row.id;
                            }}
                            title="Resource Sections"
                            rowReordering={{ isEnabled: true }}
                            addRecordButtonConfig={{
                                addRecordButtonText: 'Add Section',
                                addRecordCallBack: handleAddSection,
                            }}
                            pinnedRow={tableData.pinnedRows}
                            rowOrderChange={handleRowOrderChange}
                        />
                    )}
                </Grid>
            </Grid>

            <ModalBox
                handleCloseModal={() => {
                    return setShowAddEditResourceModal(false);
                }}
                open={showAddEditResourceModal}
                widthSize="800px"
                title={modalTitle}
            >
                <ResourceSectionForm
                    initialValues={initialValues}
                    onCancel={() => {
                        setShowAddEditResourceModal(false);
                    }}
                    onSubmit={async (formik) => {
                        handleSubmit(formik);
                    }}
                    validationSchema={partnerResourceSectionValidationSchema}
                    isViewModal={isViewResourceSectionModal}
                    setModalTitle={setModalTitle}
                    setIsEdit={setIsEdit}
                />
            </ModalBox>

            <ModalBox
                handleCloseModal={() => {
                    return setShowDeleteResourceModal({
                        open: false,
                        id: null,
                        sectionName: '',
                    });
                }}
                open={showDeleteResourceModal.open}
                widthSize="500px"
                title="Delete Section?"
            >
                <Flex gap={4} flexDirection="column" padding="16px 8px">
                    <Typography sx={{ fontSize: '16px' }} pt={2}>
                        Are you sure you want to delete {showDeleteResourceModal.sectionName}{' '}
                        section?
                    </Typography>

                    <Flex gap="24px" justifyContent={'center'}>
                        <Button
                            onClick={() => {
                                return setShowDeleteResourceModal({
                                    open: false,
                                    id: null,
                                    sectionName: '',
                                });
                            }}
                            variant="main"
                        >
                            Cancel
                        </Button>

                        <Button
                            onClick={(event) => {
                                event.preventDefault();
                                deleteResourceSectionMutation({
                                    variables: {
                                        input: {
                                            id: showDeleteResourceModal.id,
                                            sectionName: showDeleteResourceModal.sectionName,
                                        },
                                    },
                                });
                            }}
                            variant="main"
                        >
                            Confirm
                        </Button>
                    </Flex>
                </Flex>
            </ModalBox>
        </PageContent>
    );
};

export const changeRowOrder = (
    params: Pick<GridRowOrderChangeParams, 'oldIndex' | 'targetIndex'>,
    tableData: { rows: any[]; pinnedRows: { top: unknown[] } }
) => {
    const { oldIndex, targetIndex } = params;
    const rows = [...tableData.rows];

    // Ensure the pinned row is always first and set its order to 1
    const pinnedRowIndex = rows.findIndex((row) => {
        return row.pinnedAt;
    });
    if (pinnedRowIndex !== -1) {
        const [pinnedRow] = rows.splice(pinnedRowIndex, 1);
        pinnedRow.order = 1;
        rows.unshift(pinnedRow);
    }

    const movedRow = rows.splice(pinnedRowIndex !== -1 ? oldIndex + 1 : oldIndex, 1)[0];
    rows.splice(pinnedRowIndex !== -1 ? targetIndex + 1 : targetIndex, 0, movedRow);

    // Reassign order values sequentially starting from 1 (excluding the pinned row)
    rows.forEach((row, index) => {
        if (row.pinnedAt === null) {
            row.order = index + 1;
        }
    });

    tableData.rows = rows;
};

export default ConfigurePartnerResourcesPage;
